package tta.destinigo.talktoastro.shared.remote

import io.ktor.client.*
import io.ktor.client.plugins.websocket.*
import io.ktor.websocket.*
import kotlinx.serialization.*
import kotlinx.serialization.json.*
import kotlinx.coroutines.*
import kotlin.math.pow

@Serializable
data class PusherEvent(
    val event: String,
    val data: String,
    val channel: String? = null
)

@Serializable
data class ConnectionData(
    @SerialName("socket_id") val socketId: String,
    @SerialName("activity_timeout") val activityTimeout: Int
)

@Serializable
data class SubscribeData(
    val channel: String,
    val auth: String
)

@Serializable
data class PusherError(
    val code: Int,
    val message: String
)

class PusherWebSocketService(
    private val client: HttpClient,
    private val json: Json,
    private val appKey: String,
    private val cluster: String
) {
    private var session: DefaultClientWebSocketSession? = null
    private val eventListeners = mutableMapOf<String, (String) -> Unit>()
    private val subscribedChannels = mutableSetOf<String>()
    private var socketId: String? = null
    private var reconnectAttempts = 0
    private var isConnected = false
    private var shouldReconnect = true

    suspend fun connect() {
        val url = "wss://ws-$cluster.pusher.com:443/app/$appKey?protocol=7&client=ktor&version=1.0.0"

        try {
            client.webSocket(urlString = url) {
                session = this
                isConnected = true
                reconnectAttempts = 0
                handleConnection()
            }
        } catch (e: Exception) {
            handleDisconnect()
        }
    }

    private suspend fun handleConnection() {
        session?.let { socket ->
            try {
                while (socket.isActive && shouldReconnect) {
                    when (val frame = socket.incoming.receive()) {
                        is Frame.Text -> handleMessage(frame.readText())
                        is Frame.Close -> break
                        else -> Unit
                    }
                }
            } finally {
                handleDisconnect()
            }
        }
    }

    private suspend fun handleMessage(message: String) {
        try {
            val event = json.decodeFromString<PusherEvent>(message)
            when (event.event) {
                "pusher:connection_established" -> handleConnectionEstablished(event.data)
                "pusher_internal:subscription_succeeded" -> handleSubscriptionSucceeded(event)
                "pusher:ping" -> handlePing()
                "pusher:error" -> handleError(event.data)
                else -> triggerEvent(event.event)
            }
        } catch (e: Exception) {
            triggerError("Message handling failed: ${e.message}")
        }
    }

    private fun handleConnectionEstablished(data: String) {
        val connectionData = json.decodeFromString<ConnectionData>(data)
        socketId = connectionData.socketId
        triggerEvent("connected", connectionData.socketId)
    }

    private fun handleSubscriptionSucceeded(event: PusherEvent) {
        event.channel?.let { channel ->
            subscribedChannels.add(channel)
            triggerEvent("subscription_succeeded:$channel", "")
            triggerEvent("subscription_succeeded", channel)
        }
    }

    private suspend fun handlePing() {
        session?.send(Frame.Text(json.encodeToString(PusherEvent("pusher:pong", ""))))
    }

    private fun handleError(errorData: String) {
        try {
            val error = json.decodeFromString<PusherError>(errorData)
            triggerEvent("error:${error.code}", error.message)
        } catch (e: Exception) {
            triggerError("Error parsing failed: $errorData")
        }
    }

    suspend fun subscribe(channel: String, authToken: String) {
        val message = PusherEvent(
            event = "pusher:subscribe",
            data = json.encodeToString(
                SubscribeData(
                    channel = channel,
                    auth = authToken
                )
            )
        )  // Closing parenthesis was missing here

        session?.let { webSocketSession ->
            webSocketSession.send(Frame.Text(json.encodeToString(message)))
        } ?: throw IllegalStateException("Not connected to WebSocket")
    }

    fun on(event: String, callback: (String) -> Unit) {
        eventListeners[event] = callback
    }

    fun isSubscribed(channel: String): Boolean {
        return subscribedChannels.contains(channel)
    }

    suspend fun disconnect() {
        shouldReconnect = false
        session?.close()
        session = null
        isConnected = false
    }

    private fun triggerEvent(event: String, data: String = "") {
        eventListeners[event]?.invoke(data)
    }

    private fun triggerError(message: String) {
        eventListeners["error"]?.invoke(message)
    }

    private suspend fun handleDisconnect() {
        isConnected = false
        session = null
        if (shouldReconnect) {
            reconnectAttempts++
            val delayTime = minOf(1000 * 2.pow(reconnectAttempts), 30000).toLong()
            delay(delayTime)
            connect()
        }
    }

    private fun Int.pow(exponent: Int): Int =
        toDouble().pow(exponent).toInt()
}